import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { useLoader } from '../../../helpers/useLoader';
import { SortingDirection } from '../../../models/sortingDirection.model';
import { UsersList } from '../components/UsersList';
import { IProjectDetailsBl } from '../projectDetails.bl';

export const UsersListContainer = observer((): ReactElement => {
  const projectDetailsBl = useInjection(IProjectDetailsBl.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const settingsContext = useContext(Settings);
  const { projectId } = useParams<{ projectId: string }>();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userId, setUserId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const loadProjectDetailsMembers = async (): Promise<void> => {
      await withLoaderAsync(async () => {
        await projectDetailsBl.getProjectDetailsMembersAsync(projectId);
      });
    };
    void loadProjectDetailsMembers();
  }, [projectId]);

  const handleRemoveUserFromProjectConfirmed = async (): Promise<void> => {
    await withLoaderAsync(async () => {
      if (userId !== undefined) {
        await projectDetailsBl.removeUserFromProjectAsync(userId, projectId);
      }
      setIsDeleteModalOpen(false);
    }, 'delete-user-button');
  };

  const handleRemoveUserFromProjectCanceled = (): void => {
    setIsDeleteModalOpen(false);
  };

  const handleRemoveUserFromProject = (userId: string): void => {
    setUserId(userId);
    setIsDeleteModalOpen(true);
  };

  const handleSortChange = (orderBy: string, orderType: SortingDirection): void => {
    projectDetailsBl.usersSortChanged(orderBy, orderType);
  };

  const handlePaginationChange = (pageNumber: number, pageSize: number): void => {
    settingsContext.setUsersPageSize(pageSize);
    projectDetailsBl.usersPaginationChange(pageNumber, pageSize);
  };

  return (
    <>
      <UsersList
        users={projectDetailsBl.store.usersList}
        onRemoveUser={handleRemoveUserFromProject}
        onSortChange={handleSortChange}
        isLoading={isLoading}
        orderBy={projectDetailsBl.store.usersOrderBy}
        orderDirection={projectDetailsBl.store.usersOrderDirection}
        pagination={{
          ...projectDetailsBl.store.usersPagination,
          pageSize: settingsContext.store.usersPageSize,
          onChange: handlePaginationChange,
        }}
      />
      <ConfirmationModal
        showModal={isDeleteModalOpen}
        confirmationHeader={'delete_user_from_project_confirmation_header'}
        confirmationQuestion={'delete_user_from_project_confirmation_message'}
        onCancel={handleRemoveUserFromProjectCanceled}
        onConfirm={handleRemoveUserFromProjectConfirmed}
        confirmationYes={'remove'}
        loaderKey='delete-user-button'
      />
    </>
  );
});
